import { useState, useEffect } from 'react';
import api from '../../services/api';

interface ProductSalesModalProps {
    isOpen: boolean;
    onClose: () => void;
    productUnitId?: string;
    invoiceItemId?: string;
    onSuccess?: () => void;
}

export default function ProductSalesModal({ isOpen, onClose, productUnitId, invoiceItemId, onSuccess }: ProductSalesModalProps) {
    const [activeTab, setActiveTab] = useState('cost');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [masterDataError, setMasterDataError] = useState<string | null>(null);
    const [masterData, setMasterData] = useState({
        financeCompanies: [],
        insuranceCompanies: [],
        rtoAgents: [],
        exchangeDealers: []
    });

    // Form States
    const [costData, setCostData] = useState({
        base_price: 0,
        gst_amount: 0,
        rto_cost: 0,
        insurance_cost: 0,
        accessories_cost: 0,
        extended_warranty_cost: 0,
        fastag_cost: 0,
        discount_amount: 0,
        other_charges: 0
    });

    const [registrationData, setRegistrationData] = useState({
        registration_number: '',
        rto_agent_id: '',
        registration_date: '',
        rto_fees: 0,
        hsrp_fees: 0,
        road_tax: 0,
        hypothecation_fees: 0,
        smart_card_fees: 0,
        postal_fees: 0,
        state: ''
    });

    const [financeData, setFinanceData] = useState({
        finance_company_id: '',
        finance_amount: 0,
        down_payment: 0,
        rate_of_interest: 0,
        number_of_installments: 0,
        emi_amount: 0,
        finance_account_number: '',
        do_number: ''
    });

    const [insuranceData, setInsuranceData] = useState({
        insurance_company_id: '',
        policy_number: '',
        policy_start_date: '',
        policy_end_date: '',
        insurance_premium: 0,
        policy_type: 'comprehensive', // comprehensive, third_party, zero_dep
        idv_value: 0
    });

    const [exchangeData, setExchangeData] = useState({
        old_product_name: '',
        old_product_model: '',
        old_product_year: '',
        old_product_condition: 'good',
        exchange_value: 0,
        exchange_dealer_id: '',
        exchange_bonus: 0
    });

    const [installationData, setInstallationData] = useState({
        installation_date: '',
        technician_name: '',
        technician_phone: '',
        installation_notes: '',
        installation_status: 'pending' // pending, scheduled, completed
    });

    useEffect(() => {
        if (isOpen) {
            loadMasterData();
            loadExistingData();
        }
    }, [isOpen]);

    const loadMasterData = async () => {
        try {
            // Load each master data source independently so one failure doesn't break everything
            const results = await Promise.allSettled([
                api.get('/finance_companies'),
                api.get('/insurance_companies'),
                api.get('/rto_agents'),
                api.get('/exchange_dealers')
            ]);

            setMasterData({
                financeCompanies: results[0].status === 'fulfilled' ? (results[0].value?.data?.data || []) : [],
                insuranceCompanies: results[1].status === 'fulfilled' ? (results[1].value?.data?.data || []) : [],
                rtoAgents: results[2].status === 'fulfilled' ? (results[2].value?.data?.data || []) : [],
                exchangeDealers: results[3].status === 'fulfilled' ? (results[3].value?.data?.data || []) : []
            });

            // Check if any failed
            const failures = results.filter(r => r.status === 'rejected');
            if (failures.length > 0) {
                setMasterDataError(`Some dropdown data couldn't be loaded. You can still enter data manually.`);
            }
        } catch (error) {
            console.error('Error loading master data:', error);
            setMasterDataError('Could not load dropdown options. You can still enter data manually.');
        }
    };

    const loadExistingData = async () => {
        // TODO: Load existing data if productUnitId or invoiceItemId is present
        // This would involve fetching current state from backend
    };

    const handleSave = async () => {
        setLoading(true);
        try {


            // We'll need a unified endpoint or multiple calls. 
            // For now, let's assume multiple calls or a specific 'sales_details' controller.
            // Since we built separate resources, we might need to create them individually or
            // use a composite endpoint. Let's assume we update the ProductUnit/InvoiceItem 
            // with nested attributes or separate calls.

            // NOTE: Ideally we should use nested attributes on ProductUnit or InvoiceItem
            // For this implementation, we will assume we are updating the InvoiceItem or ProductUnit
            // which accepts these nested attributes as set up in the models.

            let endpoint = '';
            let body = {};

            if (invoiceItemId) {
                endpoint = `/invoice_items/${invoiceItemId}`;
                // For Invoice Item, we nest unit attributes if they exist
                body = {
                    invoice_item: {
                        invoice_cost_breakdown_attributes: costData,
                        exchange_product_attributes: exchangeData,
                        product_installation_attributes: installationData,
                        // Update associated unit details correctly
                        product_unit_attributes: {
                            id: productUnitId, // Pass ID if updating existing unit
                            product_unit_registration_attributes: registrationData,
                            product_unit_finance_attributes: financeData,
                            product_unit_insurances_attributes: [insuranceData] // Wrap in array for has_many
                        }
                    }
                };
            } else if (productUnitId) {
                endpoint = `/product_units/${productUnitId}`;
                body = {
                    product_unit: {
                        product_unit_registration_attributes: registrationData,
                        product_unit_finance_attributes: financeData,
                        product_unit_insurances_attributes: [insuranceData], // Wrap in array
                        product_installation_attributes: installationData,
                        // Cost breakdown might not be supported directly on ProductUnit without Invoice
                    }
                };
            }

            if (endpoint) {
                await api.put(endpoint, body);
            }

            if (onSuccess) onSuccess();
            onClose();
        } catch (error) {
            console.error('Error saving sales details:', error);
            alert('Error saving details. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    const TabButton = ({ id, label, icon }: any) => (
        <button
            onClick={() => setActiveTab(id)}
            className={`flex items-center gap-2 px-4 py-3 border-b-2 font-bold transition-all ${activeTab === id
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                }`}
        >
            <span className="text-lg">{icon}</span>
            <span>{label}</span>
        </button>
    );

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-slate-100 bg-slate-50">
                    <div>
                        <h2 className="text-2xl font-black text-slate-900">Product Sales Details</h2>
                        <p className="text-slate-500 text-sm mt-1">
                            {productUnitId ? 'Managing details for Unit ID: ' + productUnitId : 'Managing Invoice Item Details'}
                        </p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-slate-200 rounded-full transition-colors text-slate-500">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Tabs */}
                <div className="flex overflow-x-auto border-b border-slate-100 px-6 bg-white">
                    <TabButton id="cost" label="Cost Breakdown" icon="💰" />
                    {!(window as any).CURRENT_FIRM?.role_slug || (window as any).CURRENT_FIRM?.role_slug !== 'staff' ? (
                        <TabButton id="registration" label="Registration (RTO)" icon="🏍️" />
                    ) : null}
                    <TabButton id="finance" label="Finance" icon="🏦" />
                    <TabButton id="insurance" label="Insurance" icon="🛡️" />
                    <TabButton id="exchange" label="Exchange" icon="🔄" />
                    <TabButton id="installation" label="Installation" icon="🔧" />
                </div>

                {/* Master Data Error Message */}
                {masterDataError && (
                    <div className="mx-6 mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <p className="text-sm text-amber-800">⚠️ {masterDataError}</p>
                    </div>
                )}

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-8 bg-slate-50">

                    {/* COST BREAKDOWN TAB */}
                    {activeTab === 'cost' && (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100 col-span-3">
                                <h3 className="text-lg font-bold text-slate-900 mb-4">Invoice Pricing Details</h3>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                    <div>
                                        <label className="block text-sm font-bold text-slate-700 mb-1">Base Price (Excl. Tax)</label>
                                        <input type="number" className="w-full px-3 py-2 border rounded-lg"
                                            value={costData.base_price} onChange={e => setCostData({ ...costData, base_price: parseFloat(e.target.value) })} />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-bold text-slate-700 mb-1">GST Amount</label>
                                        <input type="number" className="w-full px-3 py-2 border rounded-lg"
                                            value={costData.gst_amount} onChange={e => setCostData({ ...costData, gst_amount: parseFloat(e.target.value) })} />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-bold text-slate-700 mb-1">Discount Amount</label>
                                        <input type="number" className="w-full px-3 py-2 border rounded-lg text-rose-600"
                                            value={costData.discount_amount} onChange={e => setCostData({ ...costData, discount_amount: parseFloat(e.target.value) })} />
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100 col-span-3">
                                <h3 className="text-lg font-bold text-slate-900 mb-4">Additional Charges</h3>
                                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                                    <div>
                                        <label className="block text-sm font-bold text-slate-700 mb-1">RTO Cost</label>
                                        <input type="number" className="w-full px-3 py-2 border rounded-lg"
                                            value={costData.rto_cost} onChange={e => setCostData({ ...costData, rto_cost: parseFloat(e.target.value) })} />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-bold text-slate-700 mb-1">Insurance Cost</label>
                                        <input type="number" className="w-full px-3 py-2 border rounded-lg"
                                            value={costData.insurance_cost} onChange={e => setCostData({ ...costData, insurance_cost: parseFloat(e.target.value) })} />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-bold text-slate-700 mb-1">Accessories</label>
                                        <input type="number" className="w-full px-3 py-2 border rounded-lg"
                                            value={costData.accessories_cost} onChange={e => setCostData({ ...costData, accessories_cost: parseFloat(e.target.value) })} />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-bold text-slate-700 mb-1">Extended Warranty</label>
                                        <input type="number" className="w-full px-3 py-2 border rounded-lg"
                                            value={costData.extended_warranty_cost} onChange={e => setCostData({ ...costData, extended_warranty_cost: parseFloat(e.target.value) })} />
                                    </div>
                                </div>
                            </div>

                            {/* Total Calculator */}
                            <div className="col-span-3 bg-indigo-50 p-6 rounded-xl border border-indigo-100 flex justify-between items-center">
                                <div>
                                    <p className="text-sm font-bold text-indigo-600 uppercase tracking-wider">Total On-Road Price</p>
                                    <p className="text-xs text-indigo-400">Sum of all costs minus discount</p>
                                </div>
                                <div className="text-3xl font-black text-indigo-900">
                                    ₹ {(
                                        (costData.base_price || 0) +
                                        (costData.gst_amount || 0) +
                                        (costData.rto_cost || 0) +
                                        (costData.insurance_cost || 0) +
                                        (costData.accessories_cost || 0) +
                                        (costData.extended_warranty_cost || 0) -
                                        (costData.discount_amount || 0)
                                    ).toLocaleString('en-IN')}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* REGISTRATION TAB */}
                    {activeTab === 'registration' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="md:col-span-2 bg-blue-50 p-4 rounded-lg border border-blue-100 mb-4">
                                <p className="text-sm text-blue-800">
                                    <strong>Tip:</strong> These details are used to clear RTO pending lists. Ensure the Registration Number is correct.
                                </p>
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1">Registration Number</label>
                                <input type="text" className="w-full px-3 py-2 border rounded-lg uppercase" placeholder="MH-01-AB-1234"
                                    value={registrationData.registration_number} onChange={e => setRegistrationData({ ...registrationData, registration_number: e.target.value })} />
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1">RTO Agent</label>
                                <select className="w-full px-3 py-2 border rounded-lg"
                                    value={registrationData.rto_agent_id} onChange={e => setRegistrationData({ ...registrationData, rto_agent_id: e.target.value })}>
                                    <option value="">Select Agent</option>
                                    {masterData.rtoAgents.map((agent: any) => (
                                        <option key={agent.id} value={agent.id}>{agent.agent_name}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1">RTO Fees</label>
                                <input type="number" className="w-full px-3 py-2 border rounded-lg"
                                    value={registrationData.rto_fees} onChange={e => setRegistrationData({ ...registrationData, rto_fees: parseFloat(e.target.value) })} />
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1">Road Tax</label>
                                <input type="number" className="w-full px-3 py-2 border rounded-lg"
                                    value={registrationData.road_tax} onChange={e => setRegistrationData({ ...registrationData, road_tax: parseFloat(e.target.value) })} />
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1">HSRP Fees</label>
                                <input type="number" className="w-full px-3 py-2 border rounded-lg"
                                    value={registrationData.hsrp_fees} onChange={e => setRegistrationData({ ...registrationData, hsrp_fees: parseFloat(e.target.value) })} />
                            </div>
                        </div>
                    )}

                    {/* FINANCE TAB */}
                    {activeTab === 'finance' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1">Finance Company</label>
                                <select className="w-full px-3 py-2 border rounded-lg"
                                    value={financeData.finance_company_id} onChange={e => setFinanceData({ ...financeData, finance_company_id: e.target.value })}>
                                    <option value="">Select Bank/NBFC</option>
                                    {masterData.financeCompanies.map((fc: any) => (
                                        <option key={fc.id} value={fc.id}>{fc.company_name} ({fc.company_type})</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1">Loan Account Number</label>
                                <input type="text" className="w-full px-3 py-2 border rounded-lg"
                                    value={financeData.finance_account_number} onChange={e => setFinanceData({ ...financeData, finance_account_number: e.target.value })} />
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1">Loan Amount</label>
                                <input type="number" className="w-full px-3 py-2 border rounded-lg"
                                    value={financeData.finance_amount} onChange={e => setFinanceData({ ...financeData, finance_amount: parseFloat(e.target.value) })} />
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1">Down Payment</label>
                                <input type="number" className="w-full px-3 py-2 border rounded-lg"
                                    value={financeData.down_payment} onChange={e => setFinanceData({ ...financeData, down_payment: parseFloat(e.target.value) })} />
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1">Interest Rate (%)</label>
                                <input type="number" step="0.01" className="w-full px-3 py-2 border rounded-lg"
                                    value={financeData.rate_of_interest} onChange={e => setFinanceData({ ...financeData, rate_of_interest: parseFloat(e.target.value) })} />
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1">EMI Amount</label>
                                <input type="number" className="w-full px-3 py-2 border rounded-lg"
                                    value={financeData.emi_amount} onChange={e => setFinanceData({ ...financeData, emi_amount: parseFloat(e.target.value) })} />
                            </div>
                        </div>
                    )}

                    {/* INSURANCE TAB */}
                    {activeTab === 'insurance' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1">Insurance Provider</label>
                                <select className="w-full px-3 py-2 border rounded-lg"
                                    value={insuranceData.insurance_company_id} onChange={e => setInsuranceData({ ...insuranceData, insurance_company_id: e.target.value })}>
                                    <option value="">Select Company</option>
                                    {masterData.insuranceCompanies.map((ic: any) => (
                                        <option key={ic.id} value={ic.id}>{ic.company_name}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1">Policy Number</label>
                                <input type="text" className="w-full px-3 py-2 border rounded-lg"
                                    value={insuranceData.policy_number} onChange={e => setInsuranceData({ ...insuranceData, policy_number: e.target.value })} />
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1">Policy Type</label>
                                <select className="w-full px-3 py-2 border rounded-lg"
                                    value={insuranceData.policy_type} onChange={e => setInsuranceData({ ...insuranceData, policy_type: e.target.value })}>
                                    <option value="comprehensive">Comprehensive (1st Party)</option>
                                    <option value="third_party">Third Party Only</option>
                                    <option value="zero_dep">Zero Depreciation</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1">Premium Amount</label>
                                <input type="number" className="w-full px-3 py-2 border rounded-lg"
                                    value={insuranceData.insurance_premium} onChange={e => setInsuranceData({ ...insuranceData, insurance_premium: parseFloat(e.target.value) })} />
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1">Policy Start Date</label>
                                <input type="date" className="w-full px-3 py-2 border rounded-lg"
                                    value={insuranceData.policy_start_date} onChange={e => setInsuranceData({ ...insuranceData, policy_start_date: e.target.value })} />
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1">Policy End Date</label>
                                <input type="date" className="w-full px-3 py-2 border rounded-lg"
                                    value={insuranceData.policy_end_date} onChange={e => setInsuranceData({ ...insuranceData, policy_end_date: e.target.value })} />
                            </div>
                        </div>
                    )}

                    {/* EXCHANGE TAB */}
                    {activeTab === 'exchange' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1">Old Product Name</label>
                                <input type="text" className="w-full px-3 py-2 border rounded-lg" placeholder="e.g. iPhone 11"
                                    value={exchangeData.old_product_name} onChange={e => setExchangeData({ ...exchangeData, old_product_name: e.target.value })} />
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1">Model / Variant</label>
                                <input type="text" className="w-full px-3 py-2 border rounded-lg" placeholder="e.g. 64GB Black"
                                    value={exchangeData.old_product_model} onChange={e => setExchangeData({ ...exchangeData, old_product_model: e.target.value })} />
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1">Exchange Value</label>
                                <input type="number" className="w-full px-3 py-2 border rounded-lg"
                                    value={exchangeData.exchange_value} onChange={e => setExchangeData({ ...exchangeData, exchange_value: parseFloat(e.target.value) })} />
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1">Exchange Partner / Dealer</label>
                                <select className="w-full px-3 py-2 border rounded-lg"
                                    value={exchangeData.exchange_dealer_id} onChange={e => setExchangeData({ ...exchangeData, exchange_dealer_id: e.target.value })}>
                                    <option value="">Select Dealer</option>
                                    {masterData.exchangeDealers.map((ed: any) => (
                                        <option key={ed.id} value={ed.id}>{ed.dealer_name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    )}

                    {/* INSTALLATION TAB */}
                    {activeTab === 'installation' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1">Installation Status</label>
                                <select className="w-full px-3 py-2 border rounded-lg"
                                    value={installationData.installation_status} onChange={e => setInstallationData({ ...installationData, installation_status: e.target.value })}>
                                    <option value="pending">Pending</option>
                                    <option value="scheduled">Scheduled</option>
                                    <option value="completed">Completed</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1">Scheduled Date</label>
                                <input type="date" className="w-full px-3 py-2 border rounded-lg"
                                    value={installationData.installation_date} onChange={e => setInstallationData({ ...installationData, installation_date: e.target.value })} />
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1">Technician Name</label>
                                <input type="text" className="w-full px-3 py-2 border rounded-lg"
                                    value={installationData.technician_name} onChange={e => setInstallationData({ ...installationData, technician_name: e.target.value })} />
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1">Notes</label>
                                <textarea className="w-full px-3 py-2 border rounded-lg" rows={3}
                                    value={installationData.installation_notes} onChange={e => setInstallationData({ ...installationData, installation_notes: e.target.value })} />
                            </div>
                        </div>
                    )}

                </div>

                {/* Footer */}
                <div className="p-6 border-t border-slate-100 bg-white flex justify-end gap-3">
                    <button onClick={onClose} className="px-6 py-2.5 font-bold text-slate-600 hover:bg-slate-100 rounded-xl transition-all">
                        Cancel
                    </button>
                    <button onClick={handleSave} disabled={loading}
                        className="px-8 py-2.5 font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl shadow-lg hover:shadow-xl transition-all disabled:opacity-50">
                        {loading ? 'Saving...' : 'Save Details'}
                    </button>
                </div>
            </div>
        </div>
    );
}
